import { useNavigate } from 'react-router-dom';
import { useOutlet } from '../contexts/OutletContext';
import { FiArrowRight, FiCheckCircle } from 'react-icons/fi';
import { MdBusiness, MdPublic, MdMap, MdLocationCity, MdStore } from 'react-icons/md';

function OutletSelectionView() {
  const {
    selectedBusiness,
    selectedCountry,
    selectedState,
    selectedCity,
    selectedOutlet,
    businesses,
    countries,
    states,
    cities,
    outlets,
    loading,
    error,
    handleSelectBusiness,
    handleSelectCountry,
    handleSelectState,
    handleSelectCity,
    handleSelectOutlet,
  } = useOutlet();

  const navigate = useNavigate();

  const onBusinessChange = (e) => {
    const business = businesses.find(b => b.id === e.target.value);
    handleSelectBusiness(business);
  };

  const onCountryChange = (e) => {
    const country = countries.find(c => c.id === e.target.value);
    handleSelectCountry(country);
  };

  const onStateChange = (e) => {
    const state = states.find(s => s.id === e.target.value);
    handleSelectState(state);
  };

  const onCityChange = (e) => {
    const city = cities.find(c => c.id === e.target.value);
    handleSelectCity(city);
  };

  const onOutletChange = (e) => {
    const outlet = outlets.find(o => o.id === e.target.value);
    handleSelectOutlet(outlet);
  };

  const handleContinue = () => {
    if (selectedOutlet) {
      navigate('/devices');
    }
  };

  const isComplete = selectedBusiness && selectedCountry && selectedState && selectedCity && selectedOutlet;

  return (
    <div className="min-vh-100 d-flex align-items-center justify-content-center" 
         style={{ background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)' }}>
      <div className="container">
        <div className="row justify-content-center">
          <div className="col-lg-8 col-xl-7">
            <div className="card shadow-lg border-0 rounded-4">
              <div className="card-body p-5">
                
                {/* Header */}
                <div className="text-center mb-5">
                  <div className="bg-primary bg-gradient rounded-circle d-inline-flex align-items-center justify-content-center mb-3"
                       style={{ width: '70px', height: '70px' }}>
                    <MdStore size={35} className="text-white" />
                  </div>
                  <h2 className="fw-bold mb-2">Select Your Outlet</h2>
                  <p className="text-muted">Choose all levels to access your terminal dashboard</p>
                </div>

                {/* Error Alert */}
                {error && (
                  <div className="alert alert-danger" role="alert">
                    <i className="bi bi-exclamation-triangle-fill me-2"></i>
                    {error}
                  </div>
                )}

                {/* Dropdown Selectors */}
                <div className="row g-4 mb-4">
                  {/* Business */}
                  <div className="col-12">
                    <label className="form-label fw-semibold d-flex align-items-center">
                      <MdBusiness className="me-2 text-primary" size={20} />
                      Business
                    </label>
                    <select 
                      className="form-select form-select-lg"
                      value={selectedBusiness?.id || ''}
                      onChange={onBusinessChange}
                      disabled={loading || businesses.length === 0}
                    >
                      <option value="">-- Select Business --</option>
                      {businesses.map((business) => (
                        <option key={business.id} value={business.id}>
                          {business.name} {business.isActive && '✓'}
                        </option>
                      ))}
                    </select>
                    {businesses.length === 0 && !loading && (
                      <small className="text-danger">No businesses found</small>
                    )}
                  </div>

                  {/* Country */}
                  <div className="col-12">
                    <label className="form-label fw-semibold d-flex align-items-center">
                      <MdPublic className="me-2 text-primary" size={20} />
                      Country
                    </label>
                    <select 
                      className="form-select form-select-lg"
                      value={selectedCountry?.id || ''}
                      onChange={onCountryChange}
                      disabled={!selectedBusiness || loading}
                    >
                      <option value="">-- Select Country --</option>
                      {countries.map((country) => (
                        <option key={country.id} value={country.id}>
                          {country.name} ({country.code})
                        </option>
                      ))}
                    </select>
                  </div>

                  {/* State */}
                  <div className="col-12">
                    <label className="form-label fw-semibold d-flex align-items-center">
                      <MdMap className="me-2 text-primary" size={20} />
                      State
                    </label>
                    <select 
                      className="form-select form-select-lg"
                      value={selectedState?.id || ''}
                      onChange={onStateChange}
                      disabled={!selectedCountry || loading}
                    >
                      <option value="">-- Select State --</option>
                      {states.map((state) => (
                        <option key={state.id} value={state.id}>
                          {state.name} ({state.code})
                        </option>
                      ))}
                    </select>
                  </div>

                  {/* City */}
                  <div className="col-12">
                    <label className="form-label fw-semibold d-flex align-items-center">
                      <MdLocationCity className="me-2 text-primary" size={20} />
                      City
                    </label>
                    <select 
                      className="form-select form-select-lg"
                      value={selectedCity?.id || ''}
                      onChange={onCityChange}
                      disabled={!selectedState || loading}
                    >
                      <option value="">-- Select City --</option>
                      {cities.map((city) => (
                        <option key={city.id} value={city.id}>
                          {city.name} {city.postalCode && `(${city.postalCode})`}
                        </option>
                      ))}
                    </select>
                  </div>

                  {/* Outlet */}
                  <div className="col-12">
                    <label className="form-label fw-semibold d-flex align-items-center">
                      <MdStore className="me-2 text-primary" size={20} />
                      Outlet
                    </label>
                    <select 
                      className="form-select form-select-lg"
                      value={selectedOutlet?.id || ''}
                      onChange={onOutletChange}
                      disabled={!selectedCity || loading}
                    >
                      <option value="">-- Select Outlet --</option>
                      {outlets.map((outlet) => (
                        <option key={outlet.id} value={outlet.id}>
                          {outlet.name} {outlet.isActive && '✓'}
                        </option>
                      ))}
                    </select>
                  </div>
                </div>


                {/* Selected Path */}
                {isComplete && (
                  <div className="alert alert-success mb-4">
                    <div className="d-flex align-items-center mb-2">
                      <FiCheckCircle className="me-2" size={20} />
                      <strong>Selection Complete!</strong>
                    </div>
                    <small className="text-muted">
                      {selectedBusiness.name} <FiArrowRight className="mx-1" /> 
                      {selectedCountry.name} <FiArrowRight className="mx-1" /> 
                      {selectedState.name} <FiArrowRight className="mx-1" /> 
                      {selectedCity.name} <FiArrowRight className="mx-1" /> 
                      {selectedOutlet.name}
                    </small>
                  </div>
                )}

                {/* Loading */}
                {loading && (
                  <div className="text-center my-3">
                    <div className="spinner-border text-primary" role="status">
                      <span className="visually-hidden">Loading...</span>
                    </div>
                  </div>
                )}

                {/* Continue Button */}
                <button
                  className="btn btn-primary btn-lg w-100 fw-semibold"
                  disabled={!isComplete || loading}
                  onClick={handleContinue}
                >
                  <FiCheckCircle className="me-2" />
                  Continue to Dashboard
                  <FiArrowRight className="ms-2" />
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

export default OutletSelectionView;
